import * as React from "react";
import * as riskService from "source/models/services/riskService";
import { AppUtils } from "source/framework/appUtils";
import { Translations } from "ui/Scripts/source/models/translations";
import { Box, Grid } from "@mui/material";
import { FilterSearchField } from "source/components/framework/Filters/FilterSearchField";
import { SaveDataObj } from "source/framework/saveData";
import { Base } from "ui/Scripts/source/framework/base";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { FilterButtons } from "source/components/framework/Filters/FilterButtons";
import { RiskStateIdFilterActive, IRiskPageFilterLists, IRiskPageFilterState, cloneRiskPageFilterState } from "./RiskPageUtils";
import { CheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/CheckboxDropdown";
import { IRiskSettingEdit } from "source/models/riskSetting/riskSettingEdit";
import { IReportItem } from "source/models/report/reportItem";
import { ISaveReportFilters } from "source/components/report/PageNavMenuItemReportList/SaveReportDialog";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { ITaskEditTypeItem, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { RiskPageFilterDrawer } from "./RiskPageFilterDrawer";
import { TaskDynamicForm } from "source/components/task/TaskDynamicForm/TaskDynamicForm";
import { useEffect, useRef, useState } from "react";

interface IRiskPageReportFilterDrawerProp extends IRiskPageFilterLists, IRiskPageFilterState {
    companyId: string;
    report: IReportItem;
    canRemoveSharedReports: boolean;
    stateIsReadOnly?: boolean;
    onSaveReport: (saveAs: boolean, onGetReportSaveFilters: () => ISaveReportFilters) => void;
    onRemoveReport: (filterId: string) => void;
    onOk: (filterState: IRiskPageFilterState, parameters: ITaskDataRow) => any;
}

interface IRiskPageReportFilterDrawerState extends IRiskPageFilterState {
    settings: IRiskSettingEdit;
    taskType: ITaskEditTypeItem; //custom parameters schema
    taskDataRow: ITaskDataRow; //custom parameter values
    extraParameters: ITaskDataRow;
}

export const RiskPageReportFilterDrawer = (props: IRiskPageReportFilterDrawerProp) => {
    const orgStateHash = useRef("");

    const getStateChangeFromProps = (props: IRiskPageReportFilterDrawerProp, state: IRiskPageReportFilterDrawerState) => {
        const taskDataRow = props.report?.taskDataRow;
        return {
            ...cloneRiskPageFilterState(props),
            taskDataRow,
            extraParameters: state?.taskType && taskDataRow
                ? new TaskDataRow(taskDataRow)
                : null,
        };
    };

    const getSaveDataFromState = (state: IRiskPageReportFilterDrawerState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("selectedRiskTypeIds", state.selectedRiskTypeIds);
        result.append("selectedUnitIds", state.selectedUnitIds);
        result.append("selectedStateIds", state.selectedStateIds);
        result.append("selectedRiskCategoryIds", state.selectedRiskCategoryIds);
        result.append("selectedRiskProcessIds", state.selectedRiskProcessIds);
        result.append("selectedRiskStandardIds", state.selectedRiskStandardIds);
        result.append("selectedRiskStakeholderIds", state.selectedRiskStakeholderIds);
        result.append("selectedResidualRiskIds", state.selectedResidualRiskIds);
        result.append("selectedRiskTreatmentStateIds", state.selectedRiskTreatmentStateIds);
        result.append("selectedHandlerUserCodes", state.selectedHandlerUserCodes);
        result.append("selectedOwners", state.selectedOwners);
        if (state.taskType && state.taskDataRow) {
            result.append("parameters", state.extraParameters.cols.map(i => state.extraParameters.getTaskDataRowColUiValue(i.field, state.taskType)));
        }
        return result;
    };

    const [state, setState] = useState<IRiskPageReportFilterDrawerState>(() => {
        const newState = {
            ...getStateChangeFromProps(props, null),
            settings: null,
            taskType: null,
        };
        orgStateHash.current = getSaveDataFromState(newState).hash;
        return newState;
    });

    useEffect(() => {
        const loadSettings = async () => {
            const settings = await AppUtils.callService(() => riskService.getRiskSettings(props.companyId), false);
            if (!settings) return;
            setState(old => ({
                ...old,
                settings,
            }));
        };

        loadSettings();
    }, []);

    useEffect(() => {
        const taskType = new TaskEditTypeItem(props.report?.taskType);
        const taskDataRow = props.report?.taskDataRow;
        setState(old => {
            const newState = {
                ...old,
                ...getStateChangeFromProps(props, old),
                taskType,
                extraParameters: taskType && taskDataRow
                    ? new TaskDataRow(taskDataRow)
                    : null,
            };
            orgStateHash.current = getSaveDataFromState(newState).hash;
            return newState;
        });
    }, [props.report?.getHash() ?? ""]);

    const handleChangeRiskTypeIds = (selectedRiskTypeIds: string[]) => {
        setState(old => ({
            ...old,
            selectedRiskTypeIds
        }));
    };

    const handleChangeUnitIds = (selectedUnitIds: string[]) => {
        setState(old => ({
            ...old,
            selectedUnitIds
        }));
    };

    const handleChangeStateIds = (selectedStateIds: string[]) => {
        setState(old => ({
            ...old,
            selectedStateIds
        }));
    };

    const handleChangedResidualRiskIds = (selectedResidualRiskIds: string[]) => {
        setState(old => ({
            ...old,
            selectedResidualRiskIds
        }));
    };

    const handleChangedRiskTreatmentStateIds = (selectedRiskTreatmentStateIds: string[]) => {
        setState(old => ({
            ...old,
            selectedRiskTreatmentStateIds
        }));
    };

    const handleChangeRiskCategoryIds = (selectedRiskCategoryIds: string[]) => {
        setState(old => ({
            ...old,
            selectedRiskCategoryIds
        }));
    };

    const handleChangeRiskProcessIds = (selectedRiskProcessIds: string[]) => {
        setState(old => ({
            ...old,
            selectedRiskProcessIds
        }));
    };

    const handleChangeRiskStandardIds = (selectedRiskStandardIds: string[]) => {
        setState(old => ({
            ...old,
            selectedRiskStandardIds
        }));
    };

    const handleChangeRiskStakeholderIds = (selectedRiskStakeholderIds: string[]) => {
        setState(old => ({
            ...old,
            selectedRiskStakeholderIds
        }));
    };

    const handleChangeHandlerUserCodes = (selectedHandlerUserCodes: string[]) => {
        setState(old => ({
            ...old,
            selectedHandlerUserCodes
        }));
    };

    const handleChangeOwners = (selectedOwners: string[]) => {
        setState(old => ({
            ...old,
            selectedOwners
        }));
    };

    const handleRefreshParameters = (taskDataRowCols: ITaskDataRowCol[]) => {
        if (!state.extraParameters) return;
        const extraParameters = new TaskDataRow(state.extraParameters);
        extraParameters.cols = taskDataRowCols;
        setState(old => ({
            ...old,
            extraParameters
        }));
    };

    const checkErrors = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            return resolve(true);
        });
    };

    const handleSaveReport = async (saveAs: boolean) => {
        if (!await AppUtils.validate(checkErrors)) return false;
        if (!saveAs) {
            props.onOk(state, state.extraParameters);
            await props.onSaveReport(false, null);
        } else {
            await props.onSaveReport(true, () => {
                return props.onOk(state, state.extraParameters);
            });
        }
    };

    const handleClear = () => {
        const extraParameters = state.extraParameters ? new TaskDataRow(state.extraParameters) : null;
        if (extraParameters) {
            extraParameters.initializeToTaskType(state.taskType);
        }
        setState(old => ({
            ...old,
            ...RiskPageFilterDrawer.getClearedParametersState(props),
            extraParameters
        }));
    };

    const handleSave = async () => {
        if (!await AppUtils.validate(checkErrors)) return false;
        if (props.onOk) {
            props.onOk(state, state.extraParameters);
        }
    };

    if (!state.settings) return null;

    return (
        <Box width="100%" sx={{ paddingTop: "1rem" }}>
            <FilterSearchField
                filter={props.report}
                canRemoveSharedFilters={props.canRemoveSharedReports}
                isReport={true}
                onSaveFilter={handleSaveReport}
                onRemoveFilter={props.onRemoveReport}
            />
            <GridRow>
                <Grid item sm={6}>
                    <FormField title={Translations.AType}>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedRiskTypeIds}
                            toggleAll={true}
                            toggleAllId={Base.emptyGuid}
                            values={props.riskTypes}
                            onChange={handleChangeRiskTypeIds}
                        />
                    </FormField>
                </Grid>
                <Grid item sm={6}>
                    <FormField title={Translations.State}>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedStateIds}
                            toggleAll={true}
                            toggleAllId={Base.emptyGuid}
                            toggleSingleSelectIds={[RiskStateIdFilterActive]}
                            values={props.states}
                            onChange={handleChangeStateIds}
                        />
                    </FormField>
                </Grid>
                <Grid item sm={6} style={{ paddingTop: "0" }}>
                    <FormField title={Translations.Unit}>
                        <TreeCheckboxDropdown
                            required={true}
                            selectedIds={state.selectedUnitIds}
                            values={props.units}
                            onChange={handleChangeUnitIds} />
                    </FormField>
                </Grid>
                <Grid item sm={6} style={{ paddingTop: "0" }}>
                    <FormField title={Translations.RiskCategory}>
                        <TreeCheckboxDropdown
                            required={true}
                            selectedIds={state.selectedRiskCategoryIds}
                            values={props.riskCategories}
                            onChange={handleChangeRiskCategoryIds}
                        />
                    </FormField>
                </Grid>
                <Grid item sm={6} style={{ paddingTop: "0" }}>
                    <FormField title={Translations.RiskOwner}>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedOwners}
                            toggleAll={true}
                            values={props.owners}
                            onChange={handleChangeOwners}
                        />
                    </FormField>
                </Grid>
                {!!state.settings?.riskStandardInUse &&
                    <Grid item sm={6} style={{ paddingTop: "0" }}>
                        <FormField title={Translations.RiskStandard}>
                            <TreeCheckboxDropdown
                                required={true}
                                selectedIds={state.selectedRiskStandardIds}
                                values={props.riskStandards}
                                onChange={handleChangeRiskStandardIds}
                            />
                        </FormField>
                    </Grid>
                }
                {!!state.settings?.riskStakeholderInUse &&
                    <Grid item sm={6} style={{ paddingTop: "0" }}>
                        <FormField title={Translations.RiskStakeholder}>
                            <TreeCheckboxDropdown
                                required={true}
                                selectedIds={state.selectedRiskStakeholderIds}
                                values={props.riskStakeholders}
                                onChange={handleChangeRiskStakeholderIds}
                            />
                        </FormField>
                    </Grid>
                }
                {!!state.settings?.riskProcessInUse &&
                    <Grid item sm={6} style={{ paddingTop: "0" }}>
                        <FormField title={state.settings.riskProcessName || Translations.RiskProcess}>
                            <TreeCheckboxDropdown
                                required={true}
                                selectedIds={state.selectedRiskProcessIds}
                                values={props.riskProcesses}
                                onChange={handleChangeRiskProcessIds}
                            />
                        </FormField>
                    </Grid>
                }
                <Grid item sm={6} style={{ paddingTop: "0" }}>
                    <FormField title={Translations.ResidualRisk}>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedResidualRiskIds}
                            toggleAll={true}
                            toggleAllId={Base.emptyGuid}
                            values={props.residualRisks}
                            onChange={handleChangedResidualRiskIds}
                        />
                    </FormField>
                </Grid>
                <Grid item sm={6} style={{ paddingTop: "0" }}>
                    <FormField title={Translations.PersonInCharge}>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedHandlerUserCodes}
                            toggleAll={true}
                            values={props.handlers}
                            onChange={handleChangeHandlerUserCodes}
                        />
                    </FormField>
                </Grid>
                <Grid item sm={6} style={{ paddingTop: "0" }}>
                    <FormField title={Translations.RiskTreatmentState}>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedRiskTreatmentStateIds}
                            toggleAll={true}
                            toggleAllId={Base.emptyGuid}
                            values={props.riskTreatmentStates}
                            onChange={handleChangedRiskTreatmentStateIds}
                        />
                    </FormField>
                </Grid>
            </GridRow>
            {!!state.extraParameters &&
                <GridRow sx={{ margin: "1rem -1rem -1rem -1rem" }}>
                    <TaskDynamicForm
                        taskTypeCols={state.taskType?.formCols}
                        taskDataRowCols={state.extraParameters?.cols}
                        onRefreshCols={handleRefreshParameters}
                    />
                </GridRow>
            }
            <FilterButtons
                onCancel={undefined}
                onClear={handleClear}
                onSave={handleSave}
            />
        </Box>
    );
};
